import * as Tone from 'tone';
import { BASE_NOTES, HARMONY, MUSIC_SCALES } from '../../utils/constants.js';

export class HarmonyContext {
  constructor() {
    this.root = 'C';
    this.rootIdx = 0;
    this.scale = [0, 2, 4, 7, 9];
    this.scaleName = 'pentatonic';
    this.octave = 3;
    this.tempo = 72;

    this.genre = 'orchestral';
    this.timeSig = [4, 4];
    this.beatsPerBar = 4;
    this.swing = 0;

    this.chordProgression = [];
    this.chordIndex = 0;
    this.chordInterval = 4;

    this.planetSeed = 0;
    this.planetDensity = 1.0;
    this.percussionStyle = 'four-on-the-floor';
    this.rand = () => 0;

    this.beatDuration = 60 / 72;
    this.globalBeat = 0;
  }

  setPlanetMusic(music) {
    this.root = music.root;
    this.rootIdx = BASE_NOTES.indexOf(music.root);
    this.scaleName = music.scale;
    this.scale = MUSIC_SCALES[music.scale] || MUSIC_SCALES.pentatonic;
    this.octave = music.octave;
    this.tempo = music.tempo;
    this.beatDuration = 60 / this.tempo;

    this.genre = music.genre || 'orchestral';
    this.timeSig = music.timeSig || [4, 4];
    this.beatsPerBar = this.timeSig[0];
    this.swing = music.swing || 0;
  }

  resetCounters() {
    this.chordIndex = 0;
    this.globalBeat = 0;
  }

  getCurrentChord() {
    return this.chordProgression[this.chordIndex] || this.chordProgression[0] || [0, 4, 7];
  }

  getCurrentChordNotes(octave) {
    const oct = octave ?? this.octave;
    const chord = this.getCurrentChord();
    return chord.map(semi => {
      const noteIdx = (this.rootIdx + semi) % 12;
      const octShift = Math.floor((this.rootIdx + semi) / 12);
      return `${BASE_NOTES[noteIdx]}${oct + octShift}`;
    });
  }

  noteFromSemitone(semi, octave) {
    const oct = octave ?? this.octave;
    const absSemi = ((semi % 12) + 12) % 12;
    const octShift = Math.floor(semi / 12);
    const noteIdx = (this.rootIdx + absSemi) % 12;
    const extraOct = Math.floor((this.rootIdx + absSemi) / 12);
    return `${BASE_NOTES[noteIdx]}${oct + octShift + extraOct}`;
  }

  noteFromDegree(degreeIndex, octave) {
    const oct = octave ?? this.octave;
    const len = this.scale.length;
    const wrappedIdx = ((degreeIndex % len) + len) % len;
    const octaveShift = Math.floor(degreeIndex / len);
    const semitone = this.scale[wrappedIdx];
    const noteIdx = (this.rootIdx + semitone) % 12;
    const noteOctShift = Math.floor((this.rootIdx + semitone) / 12);
    return `${BASE_NOTES[noteIdx]}${oct + octaveShift + noteOctShift}`;
  }

  freqFromDegree(degreeIndex, octave) {
    return Tone.Frequency(this.noteFromDegree(degreeIndex, octave)).toFrequency();
  }

  getScaleNotes(octave) {
    const oct = octave ?? this.octave;
    return this.scale.map((_, i) => this.noteFromDegree(i, oct));
  }

  getCreatureNote(voice) {
    const degree = voice.scaleDegree ?? 0;
    const octOff = voice.octaveOffset ?? 0;
    return this.noteFromDegree(degree, this.octave + octOff);
  }

  getResponseNote(leaderDegree, responderOffset) {
    const intervals = HARMONY.RESPONSE_INTERVALS;
    const interval = intervals[responderOffset % intervals.length];
    return this.noteFromDegree(leaderDegree + interval, this.octave);
  }

  quantizeTime(time, subdivision = '16n') {
    const subDuration = Tone.Time(subdivision).toSeconds();
    return Math.ceil(time / subDuration) * subDuration;
  }

  getEffectNote(index, octave) {
    const chord = this.getCurrentChord();
    const semi = chord[index % chord.length];
    const oct = octave ?? this.octave;
    return this.noteFromSemitone(semi, oct);
  }

  getPercussiveNote(variationIndex) {
    const percDegrees = [0, 4, 0, 2, 4];
    const deg = percDegrees[variationIndex % percDegrees.length];
    return this.noteFromDegree(deg, this.octave - 1);
  }

  chordToScaleDegrees(chord) {
    const degrees = [];
    for (const semi of chord) {
      const normalSemi = ((semi % 12) + 12) % 12;
      for (let d = 0; d < this.scale.length * 2; d++) {
        const wrappedD = d % this.scale.length;
        const octShift = Math.floor(d / this.scale.length);
        const scaleSemi = (this.scale[wrappedD] + octShift * 12) % 12;
        if (scaleSemi === normalSemi) {
          degrees.push(d);
          break;
        }
      }
    }
    return degrees.length > 0 ? degrees : [0, 2, 4];
  }

  buildMelodyRhythm(rand, bpb, numNotes) {
    const template = [0];
    for (let i = 1; i < numNotes; i++) {
      const prevBeat = template[template.length - 1];
      const remaining = bpb - prevBeat;
      const step = Math.max(0.25, (remaining / (numNotes - i)) * (0.5 + rand()));
      const nextBeat = Math.min(prevBeat + step, bpb - 0.25);
      if (nextBeat > prevBeat) template.push(nextBeat);
    }
    return template;
  }

  swingBeat(beat) {
    if (this.swing <= 0) return beat;
    const eighth = Math.floor(beat * 2);
    const frac = beat * 2 - eighth;
    if (eighth % 2 === 1) {
      return (eighth + frac) / 2 + this.swing * 0.15;
    }
    return beat;
  }
}
